import type {
  AdvancedPersonFilter,
  AppUpdateRelease,
  ChronologyTree,
  Collection,
  CollectionDetail,
  CollectionSearchResponse,
  CollectionExportStatus,
  CollectionImportInspection,
  CollectionImportStatus,
  CollectionTreeNode,
  CreateCollectionDto,
  CreateMediaItemDto,
  CreatePersonDto,
  CreatePersonMarkerDto,
  DetectedFace,
  DeleteDuplicateMediaItemsResult,
  DuplicateGroup,
  ImportProtocol,
  ImportProtocolDetail,
  ListPersonFaceMarkersOptions,
  CreateRuleDto,
  ListCollectionMediaItemsOptions,
  ListMediaItemsOptions,
  MediaFoldGroup,
  MaintenanceInfo,
  MaintenanceProgressEvent,
  MediaItem,
  MediaListResponse,
  MediaType,
  MediaTypeFilter,
  MediaVisibility,
  MeResponse,
  AppUser,
  BulkUpdateCapturedAtDto,
  BulkUpdateCapturedAtResult,
  TransferMediaOwnershipResult,
  Person,
  PersonFaceMarkerListResponse,
  PersonImportMapping,
  PersonMarker,
  PersonSuggestion,
  Rule,
  StatisticsResponse,
  Tag,
  TagGroup,
  UpdateCollectionDto,
  UpdateMediaItemDto,
  UpdatePersonDto,
  UpdatePersonMarkerDto,
  UpdatePersonMarkerRatingDto,
  UpdateRuleDto,
  UserSettingEntry,
} from './api.types';
import { PersonReviewFilter } from './api.types';
import { getShowFamilyMedia } from './media-scope.service';
import { apiUrl, getBffBaseUrl } from './runtime-config';

const BASE = getBffBaseUrl();

function serializeAdvancedPersonFilter(filter?: AdvancedPersonFilter): string | undefined {
  if (!filter || filter.conditions.length === 0) return undefined;
  return JSON.stringify({
    operator: filter.operator,
    conditions: filter.conditions.map(condition => {
      const legacySize = condition.minimumSize ?? null;
      const legacyRating = condition.minimumGeneralRating ?? null;
      const faceSize = condition.faceSize ?? legacySize;
      const generalRating = condition.generalRating ?? legacyRating;
      const faceTypes = condition.faceTypes?.length
        ? condition.faceTypes
        : condition.faceType == null ? null : [condition.faceType];

      return {
        personId: condition.personId,
        negated: condition.negated,
        faceTypes: faceTypes?.length ? faceTypes : null,
        faceType: faceTypes?.length === 1 ? faceTypes[0] : null,
        faceSize: faceSize ?? null,
        faceSizeComparison: faceSize == null ? null : condition.faceSizeComparison ?? 'atLeast',
        generalRating: generalRating ?? null,
        generalRatingComparison: generalRating == null
          ? null
          : condition.generalRatingComparison ?? 'atLeast',
      };
    }),
  });
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers);
  if (!headers.has('Content-Type') && options?.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers,
  });

  if (response.status === 401) {
    window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
    return new Promise<T>(() => {});
  }

  if (!response.ok) {
    let message = `API error ${response.status}`;
    try {
      const body = await response.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      const text = await response.text().catch(() => '');
      if (text) message = `${message}: ${text}`;
    }
    throw new Error(message);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

function inferMediaType(file: File): MediaType {
  return file.type.toLowerCase().startsWith('video/') ? 1 as MediaType : 0 as MediaType;
}

function xhrUpload<T>(
  path: string,
  file: File,
  onProgress?: (percent: number, loaded: number, total: number) => void,
  extraFormFields?: Record<string, string>,
  fileFieldName = 'file',
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    form.append(fileFieldName, file);
    if (extraFormFields) {
      for (const [key, value] of Object.entries(extraFormFields)) {
        form.append(key, value);
      }
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}${path}`);
    xhr.withCredentials = true;
    xhr.responseType = 'json';

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress(Math.round((event.loaded / event.total) * 100), event.loaded, event.total);
    };

    xhr.onerror = () => reject(new Error('Upload failed.'));
    xhr.onload = () => {
      if (xhr.status === 401) {
        window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.href)}`);
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        // responseType is 'json', so responseText is not accessible here — a failed JSON
        // parse (e.g. plain-text or empty error body) yields a null xhr.response instead.
        const response = xhr.response as unknown;
        const errorMessage = response && typeof response === 'object' && 'error' in response
          ? String((response as { error?: string }).error ?? 'Upload failed.')
          : typeof response === 'string' && response
            ? response
            : `Upload failed (${xhr.status}).`;
        reject(new Error(errorMessage));
        return;
      }
      resolve(xhr.response as T);
    };

    xhr.send(form);
  });
}

export function listMediaItems(options: ListMediaItemsOptions = {}): Promise<MediaListResponse> {
  const params = new URLSearchParams({
    sortBy: options.sortBy ?? 'capturedat',
    sortDescending: String(options.sortDescending ?? true),
    page: String(options.page ?? 1),
    pageSize: String(options.pageSize ?? 25),
    mediaType: String(options.mediaType ?? 0),
  });
  if (options.dateFrom) params.set('dateFrom', options.dateFrom);
  if (options.dateTo) params.set('dateTo', options.dateTo);
  if (options.searchText) params.set('searchText', options.searchText);
  if (options.includeDescription) params.set('includeDescription', 'true');
  if (options.tagIds?.length) params.set('tagIds', options.tagIds.join(','));
  if (options.matchAllTags != null) params.set('matchAllTags', String(options.matchAllTags));
  if (options.protocolNumber != null) params.set('protocolNumber', String(options.protocolNumber));
  if (options.onlyUnknownCapturedAt) params.set('onlyUnknownCapturedAt', 'true');
  if (options.personReviewFilter) params.set('personReviewFilter', String(options.personReviewFilter));
  if (options.personIds?.length) params.set('personIds', options.personIds.join(','));
  if (options.matchAllPersons) params.set('matchAllPersons', 'true');
  if (options.onlyWithoutCollection) params.set('onlyWithoutCollection', 'true');
  if (options.onlyWithCollection) params.set('onlyWithCollection', 'true');
  params.set('showFamilyMedia', String(options.showFamilyMedia ?? getShowFamilyMedia()));
  if (options.afterId) params.set('afterId', options.afterId);
  if (options.includeFolded) params.set('includeFolded', 'true');
  if (options.onlyFolded) params.set('onlyFolded', 'true');
  if (options.hasEmbeddedMetadata != null) {
    params.set('hasEmbeddedMetadata', String(options.hasEmbeddedMetadata));
  }
  if (options.isCapturedAtTimeAccurate != null) {
    params.set('isCapturedAtTimeAccurate', String(options.isCapturedAtTimeAccurate));
  }
  const localTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (localTimeZoneId) params.set('localTimeZoneId', localTimeZoneId);
  const advancedPersonFilter = serializeAdvancedPersonFilter(options.advancedPersonFilter);
  if (advancedPersonFilter) params.set('advancedPersonFilter', advancedPersonFilter);
  return request<MediaListResponse>(`/media?${params.toString()}`);
}

export function getChronologyTree(
  searchText?: string,
  includeDescription = false,
  tagIds?: string[],
  matchAllTags = true,
  mediaType?: MediaTypeFilter,
  protocolNumber?: number,
  personReviewFilter: PersonReviewFilter = PersonReviewFilter.None,
  personIds?: string[],
  matchAllPersons = false,
  onlyWithoutCollection = false,
  onlyWithCollection = false,
  showFamilyMedia?: boolean,
  advancedPersonFilter?: AdvancedPersonFilter,
  onlyFolded = false,
  hasEmbeddedMetadata?: boolean,
  isCapturedAtTimeAccurate?: boolean,
): Promise<ChronologyTree> {
  const params = new URLSearchParams();
  if (searchText) params.set('searchText', searchText);
  if (includeDescription) params.set('includeDescription', 'true');
  if (tagIds?.length) params.set('tagIds', tagIds.join(','));
  if (matchAllTags != null) params.set('matchAllTags', String(matchAllTags));
  if (mediaType) params.set('mediaType', String(mediaType));
  if (protocolNumber != null) params.set('protocolNumber', String(protocolNumber));
  if (personReviewFilter) params.set('personReviewFilter', String(personReviewFilter));
  if (personIds?.length) params.set('personIds', personIds.join(','));
  if (matchAllPersons) params.set('matchAllPersons', 'true');
  if (onlyWithoutCollection) params.set('onlyWithoutCollection', 'true');
  if (onlyWithCollection) params.set('onlyWithCollection', 'true');
  if (onlyFolded) params.set('onlyFolded', 'true');
  if (hasEmbeddedMetadata != null) params.set('hasEmbeddedMetadata', String(hasEmbeddedMetadata));
  if (isCapturedAtTimeAccurate != null) {
    params.set('isCapturedAtTimeAccurate', String(isCapturedAtTimeAccurate));
  }
  const localTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (localTimeZoneId) params.set('localTimeZoneId', localTimeZoneId);
  params.set('showFamilyMedia', String(showFamilyMedia ?? getShowFamilyMedia()));
  const serializedAdvancedPersonFilter = serializeAdvancedPersonFilter(advancedPersonFilter);
  if (serializedAdvancedPersonFilter) params.set('advancedPersonFilter', serializedAdvancedPersonFilter);
  const qs = params.toString();
  return request<ChronologyTree>(`/media/chronology${qs ? '?' + qs : ''}`);
}

export function getMediaItem(id: string): Promise<MediaItem> {
  return request<MediaItem>(`/media/${id}`);
}

export function getMediaFoldGroup(id: string): Promise<MediaFoldGroup> {
  return request<MediaFoldGroup>(`/media/${id}/fold-group`);
}

export function foldMediaItems(
  mediaItemIds: string[],
  representativeMediaItemId: string,
): Promise<MediaFoldGroup> {
  return request<MediaFoldGroup>('/media/fold', {
    method: 'POST',
    body: JSON.stringify({ mediaItemIds, representativeMediaItemId }),
  });
}

export function unfoldMediaItem(id: string): Promise<MediaItem> {
  return request<MediaItem>('/media/unfold', {
    method: 'POST',
    body: JSON.stringify({ mediaItemId: id }),
  });
}

export function unfoldMediaGroup(id: string): Promise<MediaItem> {
  return request<MediaItem>('/media/unfold-group', {
    method: 'POST',
    body: JSON.stringify({ mediaItemId: id }),
  });
}

export function createMediaItem(dto: CreateMediaItemDto): Promise<MediaItem> {
  return request<MediaItem>('/media', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateMediaItem(id: string, dto: UpdateMediaItemDto): Promise<MediaItem> {
  return request<MediaItem>(`/media/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function bulkUpdateCapturedAt(
  dto: BulkUpdateCapturedAtDto,
): Promise<BulkUpdateCapturedAtResult> {
  return request<BulkUpdateCapturedAtResult>('/media/bulk-captured-at', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export async function deleteMediaItem(id: string): Promise<boolean> {
  const response = await request<{ success: boolean }>(`/media/${id}`, { method: 'DELETE' });
  return response.success;
}

export function transferMediaOwnership(
  mediaItemIds: string[],
  targetOwnerId: number,
): Promise<TransferMediaOwnershipResult> {
  return request<TransferMediaOwnershipResult>('/media/transfer-ownership', {
    method: 'POST',
    body: JSON.stringify({ mediaItemIds, targetOwnerId }),
  });
}

export async function uploadMediaFile(
  file: File,
  protocolNumber?: number,
  onProgress?: (percent: number, loaded: number, total: number) => void,
  visibility?: MediaVisibility,
): Promise<MediaItem> {
  const created = await createMediaItem({
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    mediaType: inferMediaType(file),
    title: '',
    description: '',
    protocolNumber: protocolNumber ?? null,
    visibility,
  });
  return xhrUpload<MediaItem>(
    `/media/${created.id}/upload`,
    file,
    onProgress,
    {
      ...(protocolNumber != null ? { protocolNumber: String(protocolNumber) } : {}),
      timeZoneOffsetMinutes: String(-new Date().getTimezoneOffset()),
    },
  );
}

export function getMediaPreviewUrl(id: string, version?: string): string {
  const suffix = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${BASE}/media/${id}/preview${suffix}`;
}

export function getMediaMediumPreviewUrl(id: string, version?: string): string {
  const suffix = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${BASE}/media/${id}/medium-preview${suffix}`;
}

export function getMediaDownloadUrl(id: string): string {
  return `${BASE}/media/${id}/download`;
}

export function getPersonMarkersExportUrl(): string {
  return `${BASE}/persons/export-markers`;
}

export function startCollectionExport(collectionId: string, excludeVideos: boolean): Promise<{ jobId: string }> {
  return request<{ jobId: string }>(`/collections/${encodeURIComponent(collectionId)}/export`, {
    method: 'POST',
    body: JSON.stringify({ excludeVideos }),
  });
}

export function getCollectionExportStatus(jobId: string): Promise<CollectionExportStatus> {
  return request<CollectionExportStatus>(`/collections/export/${encodeURIComponent(jobId)}/status`);
}

export function getCollectionExportDownloadUrl(jobId: string): string {
  return `${BASE}/collections/export/${encodeURIComponent(jobId)}/download`;
}

export function uploadCollectionImport(
  file: File,
  onProgress?: (percent: number, loaded: number, total: number) => void,
): Promise<CollectionImportInspection> {
  return xhrUpload<CollectionImportInspection>(
    '/collections/import',
    file,
    onProgress,
    undefined,
    'archive',
  );
}

export function startCollectionImport(
  jobId: string,
  mappings: Array<{ sourcePersonId: string; targetPersonId?: string | null; createNew: boolean }>,
  parentCollectionId?: string | null,
): Promise<{ jobId: string }> {
  return request<{ jobId: string }>(`/collections/import/${encodeURIComponent(jobId)}/execute`, {
    method: 'POST',
    body: JSON.stringify({ mappings, parentCollectionId: parentCollectionId ?? null }),
  });
}

export function getCollectionImportStatus(jobId: string): Promise<CollectionImportStatus> {
  return request<CollectionImportStatus>(`/collections/import/${encodeURIComponent(jobId)}/status`);
}

export function getCollectionImportMediaUrl(jobId: string, mediaItemId: string): string {
  return `${BASE}/collections/import/${encodeURIComponent(jobId)}/media/${encodeURIComponent(mediaItemId)}`;
}

export function getStatistics(): Promise<StatisticsResponse> {
  return request<StatisticsResponse>('/statistics');
}

export function listTags(sortBy = 'name', sortDescending = false): Promise<Tag[]> {
  const params = new URLSearchParams({ sortBy, sortDescending: String(sortDescending) });
  return request<Tag[]>(`/tags?${params.toString()}`);
}

export function searchTags(q: string, limit = 20, offset = 0): Promise<{ tags: Tag[]; hasMore: boolean }> {
  const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
  return request<{ tags: Tag[]; hasMore: boolean }>(`/tags/search?${params.toString()}`);
}

export function createTag(dto: { name?: string; color?: string; searchText?: string; groupId?: string | null }): Promise<Tag> {
  return request<Tag>('/tags', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateTag(id: string, dto: { name?: string; color?: string; searchText?: string; groupId?: string | null }): Promise<Tag> {
  return request<Tag>(`/tags/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deleteTag(id: string): Promise<void> {
  return request<void>(`/tags/${id}`, { method: 'DELETE' });
}

export function listTagGroups(): Promise<TagGroup[]> {
  return request<TagGroup[]>('/tag-groups');
}

export function createTagGroup(name: string): Promise<TagGroup> {
  return request<TagGroup>('/tag-groups', { method: 'POST', body: JSON.stringify({ name }) });
}

export function updateTagGroup(id: string, name: string): Promise<TagGroup> {
  return request<TagGroup>(`/tag-groups/${id}`, { method: 'PUT', body: JSON.stringify({ name }) });
}

export function deleteTagGroup(id: string): Promise<void> {
  return request<void>(`/tag-groups/${id}`, { method: 'DELETE' });
}

export function reorderTagGroups(groupIds: string[]): Promise<void> {
  return request<void>('/tag-groups/reorder', { method: 'POST', body: JSON.stringify({ groupIds }) });
}

export function reorderTagsInGroup(tagIds: string[]): Promise<void> {
  return request<void>('/tags/reorder', { method: 'POST', body: JSON.stringify({ tagIds }) });
}

export function moveTagToGroup(tagId: string, groupId: string | null, position: number): Promise<Tag> {
  return request<Tag>(`/tags/${tagId}/move`, { method: 'POST', body: JSON.stringify({ groupId: groupId ?? '', position }) });
}

export function getUserSettings(): Promise<UserSettingEntry[]> {
  return request<UserSettingEntry[]>('/usersettings');
}

export function upsertUserSetting(key: string, value: string): Promise<void> {
  return request<void>(`/usersettings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) });
}

export function getMe(): Promise<MeResponse> {
  return request<MeResponse>('/me');
}

export function getMaintenanceInfo(): Promise<MaintenanceInfo> {
  return request<MaintenanceInfo>('/maintenance/info');
}

export function listAppUpdates(): Promise<AppUpdateRelease[]> {
  return request<AppUpdateRelease[]>('/app-updates');
}

export function uploadAppUpdate(file: File): Promise<AppUpdateRelease> {
  const form = new FormData();
  form.append('file', file, file.name);
  return request<AppUpdateRelease>('/app-updates', { method: 'POST', body: form });
}

export function setAppUpdateChannels(
  currentReleaseId: string | null,
  previewReleaseId: string | null,
): Promise<AppUpdateRelease[]> {
  return request<AppUpdateRelease[]>('/app-updates/channels', {
    method: 'PUT',
    body: JSON.stringify({ currentReleaseId, previewReleaseId }),
  });
}

export function deleteAppUpdate(id: string): Promise<void> {
  return request<void>(`/app-updates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function listDuplicateGroups(): Promise<DuplicateGroup[]> {
  return request<DuplicateGroup[]>('/maintenance/duplicate-groups');
}

export function deleteDuplicateMediaItems(mediaItemIds: string[]): Promise<DeleteDuplicateMediaItemsResult> {
  return request<DeleteDuplicateMediaItemsResult>('/maintenance/duplicate-groups/delete', {
    method: 'POST',
    body: JSON.stringify({ mediaItemIds }),
  });
}

export function streamCleanupOrphanedFiles(
  deleteOrphanedDiskFiles: boolean,
  deleteMissingFileMediaItems: boolean,
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/cleanup-orphaned-files?deleteOrphanedDiskFiles=${deleteOrphanedDiskFiles}&deleteMissingFileMediaItems=${deleteMissingFileMediaItems}`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamExtractGeoLocations(
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/extract-geo-locations`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamBackfillEmbeddedMetadata(
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/backfill-embedded-metadata`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.error) {
          es.close();
          reject(new Error(payload.message || 'Maintenance stream failed.'));
          return;
        }
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamBackfillFaceEmbeddings(
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/backfill-face-embeddings`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamBackfillFaceSharpness(
  onProgress: (evt: MaintenanceProgressEvent) => void,
  recalculateExisting = false,
): Promise<void> {
  const path = `/maintenance/backfill-face-sharpness${recalculateExisting ? '?recalculateExisting=true' : ''}`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamBackfillMediumPreviews(
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/backfill-medium-previews`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamBackfillPersonMarkerPreviews(
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/backfill-person-marker-previews`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function streamClearFilenameTitles(
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  const path = `/maintenance/clear-filename-titles`;
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data as string) as MaintenanceProgressEvent;
        onProgress(payload);
        if (payload.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid maintenance event payload.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Maintenance stream failed.'));
    };
  });
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Renders a millisecond duration as a short human-readable string, e.g. "6 minutes", "45 seconds", "1 hour 5 minutes".
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !isFinite(ms) || ms < 0) return '—';
  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds} second${totalSeconds === 1 ? '' : 's'}`;
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} minute${totalMinutes === 1 ? '' : 's'}`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hoursText = `${hours} hour${hours === 1 ? '' : 's'}`;
  return minutes > 0 ? `${hoursText} ${minutes} minute${minutes === 1 ? '' : 's'}` : hoursText;
}

export function listRules(): Promise<Rule[]> {
  return request<Rule[]>('/rules');
}

export function getRule(id: string): Promise<Rule> {
  return request<Rule>(`/rules/${id}`);
}

export function createRule(dto: CreateRuleDto): Promise<Rule> {
  return request<Rule>('/rules', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateRule(id: string, dto: UpdateRuleDto): Promise<Rule> {
  return request<Rule>(`/rules/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deleteRule(id: string): Promise<void> {
  return request<void>(`/rules/${id}`, { method: 'DELETE' });
}

export function createImportProtocol(source = 'web'): Promise<ImportProtocol> {
  return request<ImportProtocol>(
    `/import-protocols?source=${encodeURIComponent(source)}`,
    { method: 'POST' },
  );
}

export function completeImportProtocol(number: number): Promise<ImportProtocol> {
  return request<ImportProtocol>(`/import-protocols/${number}/complete`, { method: 'POST' });
}

export function listImportProtocols(): Promise<ImportProtocol[]> {
  return request<ImportProtocol[]>('/import-protocols');
}

export function getImportProtocol(number: number): Promise<ImportProtocolDetail> {
  return request<ImportProtocolDetail>(`/import-protocols/${number}`);
}

export function deleteImportProtocol(number: number): Promise<void> {
  return request<void>(`/import-protocols/${number}`, { method: 'DELETE' });
}

export function listPersons(sortBy = 'usageCount', sortDescending = true): Promise<Person[]> {
  const params = new URLSearchParams({ sortBy, sortDescending: String(sortDescending) });
  return request<Person[]>(`/persons?${params.toString()}`);
}

export function listPersonImportMappings(): Promise<PersonImportMapping[]> {
  return request<PersonImportMapping[]>('/persons/import-mappings');
}

export function deletePersonImportMapping(id: string): Promise<void> {
  return request<void>(`/persons/import-mappings/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function deletePersonImportMappings(ids: string[]): Promise<{ deletedCount: number }> {
  return request<{ deletedCount: number }>('/persons/import-mappings/delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function getPerson(id: string): Promise<Person> {
  return request<Person>(`/persons/${encodeURIComponent(id)}`);
}

export function searchPersons(q: string, limit = 20, offset = 0): Promise<{ persons: Person[]; hasMore: boolean }> {
  const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
  return request<{ persons: Person[]; hasMore: boolean }>(`/persons/search?${params.toString()}`);
}

export function createPerson(dto: CreatePersonDto): Promise<Person> {
  return request<Person>('/persons', { method: 'POST', body: JSON.stringify(dto) });
}

export function updatePerson(id: string, dto: UpdatePersonDto): Promise<Person> {
  return request<Person>(`/persons/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deletePerson(id: string): Promise<void> {
  return request<void>(`/persons/${id}`, { method: 'DELETE' });
}

export function listPersonFaceMarkers(
  personId: string | null | undefined,
  options: ListPersonFaceMarkersOptions = {},
): Promise<PersonFaceMarkerListResponse> {
  const params = new URLSearchParams();
  if (options.pageSize !== undefined) params.set('pageSize', String(options.pageSize));
  if (options.afterId) params.set('afterId', options.afterId);
  if (options.sortBy) params.set('sortBy', options.sortBy);
  if (options.sortDescending !== undefined) params.set('sortDescending', String(options.sortDescending));
  if (options.anyUnratedOnly) params.set('anyUnratedOnly', 'true');
  if (options.sharpnessUnratedOnly) params.set('sharpnessUnratedOnly', 'true');
  if (options.minSharpnessRating !== undefined) params.set('minSharpnessRating', String(options.minSharpnessRating));
  if (options.maxSharpnessRating !== undefined) params.set('maxSharpnessRating', String(options.maxSharpnessRating));
  if (options.faceType !== undefined) params.set('faceType', String(options.faceType));
  if (options.generalRatingUnratedOnly) params.set('generalRatingUnratedOnly', 'true');
  if (options.minGeneralRating !== undefined) params.set('minGeneralRating', String(options.minGeneralRating));
  if (options.markerSize !== undefined) params.set('markerSize', String(options.markerSize));
  const query = params.toString();
  const path = personId
    ? `/persons/${encodeURIComponent(personId)}/face-markers`
    : '/persons/face-markers';
  return request<PersonFaceMarkerListResponse>(
    `${path}${query ? `?${query}` : ''}`,
  );
}

export function getPersonMarkerCropUrl(personId: string, markerId: string, version?: string): string {
  const query = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${BASE}/persons/${encodeURIComponent(personId)}/face-markers/${encodeURIComponent(markerId)}/crop${query}`;
}

export function updatePersonMarkerRating(
  personId: string,
  markerId: string,
  dto: UpdatePersonMarkerRatingDto,
): Promise<PersonMarker> {
  return request<PersonMarker>(
    `/persons/${encodeURIComponent(personId)}/face-markers/${encodeURIComponent(markerId)}`,
    { method: 'PUT', body: JSON.stringify(dto) },
  );
}

/** Reassigns all markers from the source person to the target person, then deletes the source
 *  person (which no longer has any markers left). Returns the (updated) target person. */
export function mergePersons(sourcePersonId: string, targetPersonId: string): Promise<Person> {
  return request<Person>(`/persons/${sourcePersonId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ targetPersonId }),
  });
}

export function listPersonMarkers(mediaItemId: string): Promise<PersonMarker[]> {
  return request<PersonMarker[]>(`/media/${mediaItemId}/markers`);
}

export function createPersonMarker(mediaItemId: string, dto: CreatePersonMarkerDto): Promise<PersonMarker> {
  return request<PersonMarker>(`/media/${mediaItemId}/markers`, { method: 'POST', body: JSON.stringify(dto) });
}

export interface DetectFacesMargins {
  topMarginPercent: number;
  sideMarginPercent: number;
  bottomMarginPercent: number;
  scoreThreshold?: number;
  maxWorkingDimension?: number;
}

export function detectFaces(mediaItemId: string, margins?: DetectFacesMargins): Promise<DetectedFace[]> {
  let query = '';
  if (margins) {
    const params = new URLSearchParams({
      topMarginPercent: String(margins.topMarginPercent),
      sideMarginPercent: String(margins.sideMarginPercent),
      bottomMarginPercent: String(margins.bottomMarginPercent),
    });
    if (margins.scoreThreshold !== undefined) params.set('scoreThreshold', String(margins.scoreThreshold));
    if (margins.maxWorkingDimension !== undefined) params.set('maxWorkingDimension', String(margins.maxWorkingDimension));
    query = `?${params.toString()}`;
  }
  return request<DetectedFace[]>(`/media/${mediaItemId}/detect-faces${query}`, { method: 'POST' });
}

/** Recomputes person suggestions for a single face rectangle (normalized 0..1) — used when the
 *  user moves/resizes a pending (not-yet-assigned) auto-detected face box, so the suggestions
 *  always reflect its current position/size instead of the stale ones from initial detection. */
export function suggestPersonsForRect(
  mediaItemId: string,
  rect: { x: number; y: number; width: number; height: number },
): Promise<PersonSuggestion[]> {
  return request<PersonSuggestion[]>(`/media/${mediaItemId}/suggest-person`, {
    method: 'POST',
    body: JSON.stringify(rect),
  });
}

export function updatePersonMarker(markerId: string, dto: UpdatePersonMarkerDto): Promise<PersonMarker> {
  return request<PersonMarker>(`/media/markers/${markerId}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deletePersonMarker(markerId: string): Promise<void> {
  return request<void>(`/media/markers/${markerId}`, { method: 'DELETE' });
}

/** Computes a person's age (in years) at the time a photo was captured. Requires a known birth year. */
export function calculateAgeAtCapture(person: Pick<Person, 'birthYear' | 'birthMonth' | 'birthDay'>, capturedAt: string | null | undefined): number | null {
  if (!capturedAt || person.birthYear == null) return null;
  const captured = new Date(capturedAt);
  if (isNaN(captured.getTime())) return null;
  let age = captured.getFullYear() - person.birthYear;
  const month = person.birthMonth ?? 1;
  const day = person.birthDay ?? 1;
  const hadBirthdayYet = captured.getMonth() + 1 > month
    || (captured.getMonth() + 1 === month && captured.getDate() >= day);
  if (!hadBirthdayYet) age--;
  return age >= 0 ? age : null;
}

/** Computes a person's age at capture time as whole years + remaining months. Requires a known birth year. */
export function calculateAgeDetailsAtCapture(
  person: Pick<Person, 'birthYear' | 'birthMonth' | 'birthDay'>,
  capturedAt: string | null | undefined,
): { years: number; months: number } | null {
  if (!capturedAt || person.birthYear == null) return null;
  const captured = new Date(capturedAt);
  if (isNaN(captured.getTime())) return null;
  const birthMonthIndex = (person.birthMonth ?? 1) - 1;
  const birthDay = person.birthDay ?? 1;
  let totalMonths = (captured.getFullYear() - person.birthYear) * 12 + (captured.getMonth() - birthMonthIndex);
  if (captured.getDate() < birthDay) totalMonths--;
  if (totalMonths < 0) return null;
  return { years: Math.floor(totalMonths / 12), months: totalMonths % 12 };
}


export function formatPersonName(person: Pick<Person, 'firstName' | 'lastName' | 'nickName'>): string {
  const full = `${person.firstName} ${person.lastName}`.trim();
  if (!person.nickName) return full;
  return full ? `${full} ${person.nickName}` : person.nickName;
}

// ── Collections ──────────────────────────────────────────────────────────────

export function listRootCollections(): Promise<Collection[]> {
  return request<Collection[]>('/collections', { cache: 'no-store' });
}

export function listChildCollections(parentId: string): Promise<Collection[]> {
  return request<Collection[]>(`/collections/${parentId}/children`, { cache: 'no-store' });
}

export function searchCollections(
  searchText: string,
  page = 1,
  pageSize = 100,
): Promise<CollectionSearchResponse> {
  const params = new URLSearchParams({
    q: searchText.trim(),
    page: String(page),
    pageSize: String(pageSize),
  });
  return request<CollectionSearchResponse>(`/collections/search?${params.toString()}`, {
    cache: 'no-store',
  });
}

export function getCollection(id: string, options: ListCollectionMediaItemsOptions = {}): Promise<CollectionDetail> {
  const params = new URLSearchParams({
    sortBy: options.sortBy ?? 'capturedat',
    sortDescending: String(options.sortDescending ?? true),
    page: String(options.page ?? 1),
    pageSize: String(options.pageSize ?? 25),
  });
  if (options.collectionSearchText?.trim()) {
    params.set('collectionSearchText', options.collectionSearchText.trim());
  }
  if (options.mediaSearchText?.trim()) {
    params.set('mediaSearchText', options.mediaSearchText.trim());
  }
  if (options.onlyFolded) params.set('onlyFolded', 'true');
  return request<CollectionDetail>(`/collections/${id}?${params.toString()}`, { cache: 'no-store' });
}

export function createCollection(dto: CreateCollectionDto): Promise<Collection> {
  return request<Collection>('/collections', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateCollection(id: string, dto: UpdateCollectionDto): Promise<Collection> {
  return request<Collection>(`/collections/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

// Flat listing of every visible collection with lightweight tree/picker metadata — used to render
// whole-tree views without a per-node round trip.
export function getCollectionTree(): Promise<CollectionTreeNode[]> {
  return request<CollectionTreeNode[]>('/collections/tree');
}

// `newParentId: null` moves the collection to the root (no parent).
export function moveCollection(id: string, newParentId: string | null): Promise<Collection> {
  return request<Collection>(`/collections/${id}/move`, { method: 'POST', body: JSON.stringify({ newParentId }) });
}

export async function deleteCollection(id: string): Promise<boolean> {
  const response = await request<{ success: boolean }>(`/collections/${id}`, { method: 'DELETE' });
  return response.success;
}

export async function addMediaItemsToCollection(collectionId: string, mediaItemIds: string[]): Promise<number> {
  const response = await request<{ success: boolean; changedCount: number }>(
    `/collections/${collectionId}/media-items`,
    { method: 'POST', body: JSON.stringify({ mediaItemIds }) },
  );
  return response.changedCount;
}

export async function removeMediaItemsFromCollection(collectionId: string, mediaItemIds: string[]): Promise<number> {
  const response = await request<{ success: boolean; changedCount: number }>(
    `/collections/${collectionId}/media-items/remove`,
    { method: 'POST', body: JSON.stringify({ mediaItemIds }) },
  );
  return response.changedCount;
}

export async function moveMediaItemsBetweenCollections(
  sourceCollectionId: string,
  targetCollectionId: string,
  mediaItemIds: string[],
): Promise<number> {
  const response = await request<{ success: boolean; changedCount: number }>(
    `/collections/${sourceCollectionId}/media-items/move`,
    {
      method: 'POST',
      body: JSON.stringify({ targetCollectionId, mediaItemIds }),
    },
  );
  return response.changedCount;
}

export function getCollectionsForMediaItem(mediaItemId: string): Promise<Collection[]> {
  return request<Collection[]>(`/media/${mediaItemId}/collections`);
}

export function generateCollectionLocationMap(collectionId: string): Promise<Collection> {
  return request<Collection>(`/collections/${collectionId}/location-map`, { method: 'POST' });
}

export function getCollectionLocationMapUrl(collectionId: string, version?: string): string {
  const suffix = version ? `?v=${encodeURIComponent(version)}` : '';
  return `${BASE}/collections/${collectionId}/location-map${suffix}`;
}

export function deleteCollectionLocationMap(collectionId: string): Promise<Collection> {
  return request<Collection>(`/collections/${collectionId}/location-map`, { method: 'DELETE' });
}

export function getAllUsers(): Promise<AppUser[]> {
  return request<AppUser[]>('/users');
}

export function getTransferTargets(): Promise<AppUser[]> {
  return request<AppUser[]>('/users/transfer-targets');
}

export function setFamilyMember(userId: number, isFamilyMember: boolean): Promise<AppUser> {
  return request<AppUser>(`/users/${userId}/family-member`, {
    method: 'PUT',
    body: JSON.stringify({ isFamilyMember }),
  });
}
